import {
  toWSDateString,
  useWSSnackbar,
  WSButton,
  WSCard,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useCashbackDetailsModal } from "../modals/useCashbackDetailsModal";
import {
  CashbackPromotion,
  CashbackPromotionRewardStatus,
  CashbackPromotionStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesCashback } from "@query";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { formatNumber } from "accounting";
import { getDaysBetween } from "../../../utils/dates";

const getPercent = (promotion?: CashbackPromotion) => {
  const currentSpend = Number(promotion?.currentSpend) || 0;
  const spendTarget = Number(promotion?.spendTarget) || 1;

  return Math.floor((currentSpend * 100) / spendTarget);
};

export const CardholderBenefits: React.FC = () => {
  const cashbackDetailsModal = useCashbackDetailsModal();
  const [redeemCashbackPromotion] =
    queriesCashback.redeemCashbackPromotion.useMutation({
      dependencies: [queriesCashback.listCashbackPromotions.queryKeyBase]
    });
  const { openSnackbar } = useWSSnackbar();

  const queryCashbackPromotion =
    queriesCashback.listCashbackPromotions.useQuery();

  // v1 returned a single "current" promotion; v3 returns a list. Prefer a live
  // (Active/Complete) promotion, falling back to the first entry so an
  // Expired-only list still renders the expired-offer notice.
  const promotions = queryCashbackPromotion.data?.data ?? [];
  const promotion =
    promotions.find(p => p.status !== CashbackPromotionStatus.EXPIRED) ??
    promotions[0];

  const percent = getPercent(promotion);
  const endDate = promotion?.endDate;
  const oneTimeCardSpend = Number(promotion?.spendTarget) || 1000;

  const promotionStatus = promotion?.status;
  const rewardStatus = promotion?.reward?.status;
  const rewardLink = promotion?.reward?.redemptionLink;

  const isRewardRedeemed =
    rewardStatus === CashbackPromotionRewardStatus.ISSUED ||
    rewardStatus === CashbackPromotionRewardStatus.PENDING;

  const isPromotionActive = promotionStatus === CashbackPromotionStatus.ACTIVE;
  const isPromotionComplete =
    promotionStatus === CashbackPromotionStatus.COMPLETE;
  const isPromotionExpired =
    promotionStatus === CashbackPromotionStatus.EXPIRED && !isRewardRedeemed;

  const isRedeemable =
    isPromotionComplete && endDate && endDate < new Date() && !isRewardRedeemed;

  const isGiftCardInProgress =
    promotionStatus === "Complete" &&
    (rewardStatus === "Pending" || rewardStatus === "Issued") &&
    !rewardLink;

  const isGiftCardReady =
    promotionStatus === "Complete" && rewardStatus === "Issued" && rewardLink;

  const waitForRedeem =
    isPromotionComplete && endDate && endDate > new Date() && !isRewardRedeemed;

  const daysLeft = getDaysBetween(new Date(), endDate || new Date());

  return (
    <WSList gap="L">
      <WSText.Paragraph weight="medium">Cardholder benefits</WSText.Paragraph>

      {isPromotionActive || isPromotionComplete ? (
        <WSCard
          header={{
            value: {
              pill: {
                text: "New",
                theme: "success",
                badge: true
              }
            },
            label: {
              text: "Bonus reward"
            }
          }}
          divider
          actions={{
            alignment: "left",
            sortButtons: false,
            buttons: [
              {
                label: "Details",
                kind: "Link",
                rightIcon: "chevron-right",
                onClick: () => {
                  cashbackDetailsModal.open();
                }
              }
            ]
          }}
        >
          <WSList gap="M">
            <WSText.Paragraph weight="medium" color="gray500">
              Earn a $50 gift card
            </WSText.Paragraph>
            {isPromotionComplete ? (
              <>
                <WSEmptyState
                  size="S"
                  orientation="horizontal"
                  type="done"
                  title="Congrats!"
                  description="You met the spend goal for this bonus reward!"
                />
                {isRedeemable ? (
                  <WSButton.Secondary
                    label="Redeem"
                    onAsyncClick={async () => {
                      if (!promotion?.id) return;

                      // Stable across retries of the same redemption so a replay
                      // after a client-side timeout is deduped server-side.
                      const idempotencyKey = new IdempotencyKey({
                        promotionId: promotion.id
                      });

                      await redeemCashbackPromotion(
                        {
                          promotionId: promotion.id,
                          idempotencyKey: idempotencyKey.value
                        },
                        {
                          onSuccess() {
                            idempotencyKey.destroy();
                            openSnackbar({
                              type: "success",
                              message: "Gift card creation initiated."
                            });
                          },
                          onError() {
                            openSnackbar({
                              type: "warning",
                              message: "Failed to initiate gift card creation!"
                            });
                          }
                        }
                      );
                    }}
                  />
                ) : waitForRedeem ? (
                  <WSInfoBox>
                    Your gift card can be redeemed after the monthly cycle
                    closes (~{daysLeft} days). Check back soon.
                  </WSInfoBox>
                ) : isGiftCardInProgress ? (
                  <WSInfoBox>
                    Gift card creation is in progress. Please check back soon.
                  </WSInfoBox>
                ) : isGiftCardReady ? (
                  <WSInfoBox>
                    <WSText.ParagraphSm>
                      Your gift card is ready!{" "}
                      <a
                        href={rewardLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Click here to access it
                      </a>
                      .
                    </WSText.ParagraphSm>
                  </WSInfoBox>
                ) : null}
              </>
            ) : (
              <>
                <WSText.ParagraphSm color="gray500">
                  when you spend ${formatNumber(oneTimeCardSpend)} by{" "}
                  {endDate
                    ? toWSDateString(endDate, "monthDate")
                    : "the end of the cycle"}
                  .
                </WSText.ParagraphSm>
                <WSProgressBar
                  noAnimation={false}
                  percent={percent}
                  text={null}
                  mt="L"
                />
                <WSInfoBox>
                  {percent}% of the way to your bonus reward
                </WSInfoBox>
              </>
            )}
          </WSList>
        </WSCard>
      ) : isPromotionExpired ? (
        <WSCard
          header={{
            value: {
              pill: {
                text: "Expired",
                theme: "neutral",
                badge: true
              }
            },
            label: {
              text: "Bonus reward"
            }
          }}
          divider
        >
          <WSList gap="M">
            <WSInfoBox>
              The spend goal for this bonus reward was not met and the offer has
              expired. Keep an eye out for future offers to be announced on your
              Wallet page.
            </WSInfoBox>
          </WSList>
        </WSCard>
      ) : null}

      <WSCard
        header={{
          label: {
            text: "Cashback"
          }
        }}
        divider
        actions={{
          alignment: "fill",
          sortButtons: false,
          buttons: [
            {
              label: "Details",
              kind: "Link",
              rightIcon: "chevron-right",
              onClick: () => {
                cashbackDetailsModal.open();
              }
            }
          ]
        }}
      >
        <WSList gap="M">
          <WSText.Paragraph weight="medium" color="gray500">
            Earn 1% cashback
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500">
            on eligible purchases every month—keep your average daily balance
            above $100.
          </WSText.ParagraphSm>
        </WSList>
      </WSCard>
    </WSList>
  );
};
